import React, { useContext, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  usePersonalInformation,
  useVerifyProxy,
} from '@sentara-health/sentara-api-hooks-core';
import {
  Button,
  Checkbox,
  DatePicker,
  ErrorMessage,
  GlobalError,
  Headings,
  HelpIcon,
  InputField,
  Label,
  Loader,
  ModalMultiUse,
  Paragraph,
} from '@sentara-health/optimahealth_web';
import {
  convertStringToDate,
  convertStrDateFormat,
  domainForSegment,
  FormTitles,
  FormTitleTranslationKeys,
  getErrorTypeByCode,
  getMaxLength,
  IdentifierType,
  IdentifierTypeValue,
  NavigateConst,
  SCREEN_NAMES,
  SpecialCharacter,
  getAnalyticsBaseConfig,
  logoutErrorHandler,
} from 'common';
import {
  ANALYTICS_MESSAGES,
  DateFormatRegex,
  getPlanType,
} from 'common/utils/constants';
import { isAgeLessThanRequired, minYears, setMaxDate, validateDatePickerInput } from 'common/utils';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { GlobalContext, isAvMed } from 'context';
import { setOpenReplayRegistrationTags } from 'common/openreplay/openreplay';
import { AdobeFormConfig } from 'common/AdobeFormConfig';
import {
  getDrIdentifierFieldConfig,
  getDrIdentifierOptions,
  getDrGuestRegistrationPath,
  getDrVerifyRequest,
  isNoContentResponse,
  shouldRedirectDrToGuestRegistration,
} from './drRegistrationUtils';

const DrPersonalInformation = ({
  formData,
  page,
  setFormData,
  setPage,
}: any) => {
  const { t } = useTranslation();
  const { logging, proxyAuthorizationLinkData } = useContext(GlobalContext);
  const isAvMedBrand = isAvMed();
  const { verifyProxy } = useVerifyProxy();
  const { PersonalInformation } = usePersonalInformation();
  const { formSubmissionSucceeded, formSubmissionFailed } = useAdobeAnalytics();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [loader, setLoader] = useState(false);
  const [identifierError, setIdentifierError] = useState('');
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [drVerifyFailureModal, setDrVerifyFailureModal] = useState(false);
  const [identifierTooltipModal, setIdentifierTooltipModal] = useState(false);
  const [identifierType, setIdentifierType] = useState<IdentifierTypeValue>(
    IdentifierType.SSN
  );

  const identifierOptions = getDrIdentifierOptions(t);
  const identifierFieldConfig = getDrIdentifierFieldConfig(t);

  const logPersonalInfoError = (response: any) => {
    const apiDetails = {
      exception: {
        errorType: getErrorTypeByCode(response?.errorCode?.toString()),
        message: response?.errorDetails,
        stack: response?.error,
      },
      response: { httpStatus: response?.errorCode },
    };

    logging?.error({
      additionalDetails: getAnalyticsBaseConfig({ userId: '' }),
      apiDetails,
      message: ANALYTICS_MESSAGES.REG_PERSONALINFO_EXCEPTION,
      screenName: SCREEN_NAMES.REGISTRATION,
    });
  };

  const methods = useForm({
    mode: 'onBlur',
    defaultValues: {
      firstName: formData[page]?.firstName ?? '',
      lastName: formData[page]?.lastName ?? '',
      dob: formData[page]?.dob ?? '',
      ssn: formData[page]?.ssn ?? '',
      memberId: formData[page]?.memberId ?? '',
      medicareId: formData[page]?.medicareId ?? '',
      medicaId: formData[page]?.medicaId ?? '',
      email: formData[page]?.email ?? '',
      confirmEmail: formData[page]?.confirmEmail ?? '',
    },
  });

  const {
    handleSubmit,
    register,
    clearErrors,
    setValue,
    watch,
    setError,
    formState: { errors, isValid },
  } = methods;

  interface InputFieldProps {
    firstName: string;
    lastName: string;
    dob: string;
    ssn: string;
    memberId: string;
    medicareId: string;
    medicaId: string;
    email: string;
    confirmEmail: string;
    [key: string]: string;
  }

  type IdentifierFieldKey = 'ssn' | 'memberId' | 'medicareId' | 'medicaId';

  const isIdentifierFieldKey = (value: string): value is IdentifierFieldKey =>
    ['ssn', 'memberId', 'medicareId', 'medicaId'].includes(value);

  const getPlanTypeByIdentifier = (type: IdentifierTypeValue) => {
    if (type === IdentifierType.MEDICARE_ID) {
      return getPlanType.medicare;
    }

    if (type === IdentifierType.MEDICAID_ID) {
      return getPlanType.medicaid;
    }

    return getPlanType.individual;
  };

  const handleIdentifierChange = (newType: IdentifierTypeValue) => {
    setIdentifierType(newType);
    identifierOptions.forEach((option) => {
      if (option.value !== newType) {
        setValue(option.fieldKey as any, '');
        clearErrors(option.fieldKey as any);
      }
    });
  };

  const validateDOB = (dob: string): boolean => {
    const formattedVal = convertStringToDate(dob);
    const isMinimumAge = isAgeLessThanRequired(new Date(dob));
    if (isMinimumAge) {
      setError('dob', { type: 'lessAge' });
      return false;
    }
    if (formattedVal === 'Invalid Date') {
      setError('dob', { type: 'invalidDate' });
      return false;
    }
    return true;
  };

  const redirectToGuestRegistration = (data?: InputFieldProps) => {
    setLoader(false);
    navigate(getDrGuestRegistrationPath(), {
      replace: true,
      state: {
        guestRegistrationPrefill: data
          ? {
              firstName: data.firstName,
              lastName: data.lastName,
              dob: data.dob,
              email: data.email,
              confirmEmail: data.confirmEmail,
              ssn: '',
              guest: data.ssn,
            }
          : undefined,
      },
    });
  };

  const openDrVerifyFailureModal = () => {
    setLoader(false);
    setDrVerifyFailureModal(true);
  };

  const closeDrVerifyFailureModal = () => {
    setDrVerifyFailureModal(false);
  };

  const onSubmit = async (data: InputFieldProps) => {
    const dobValue = String(data?.dob ?? '');
    if (!dobValue || !validateDOB(dobValue)) {
      return;
    }

    setIdentifierError('');
    setAlreadyRegistered(false);

    setLoader(true);
    const formattedDate = convertStrDateFormat(convertStringToDate(data.dob), true);

    const selectedIdentifierType = isAvMedBrand
      ? IdentifierType.SSN
      : identifierType;
    const selectedField = identifierFieldConfig[selectedIdentifierType] as any;
    if (!selectedField?.id || !isIdentifierFieldKey(selectedField.id)) {
      setLoader(false);
      setIdentifierError(t('activation.formFailReason'));
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['personalInfoError'],
          formFailReason: t('activation.formFailReason'),
        },
      });
      return;
    }
    const selectedFieldId = selectedField.id;
    const selectedValue = data[selectedFieldId] ?? '';
    const registrationVerifyPayload = {
      firstName: data.firstName,
      lastName: data.lastName,
      ssn: selectedIdentifierType === IdentifierType.SSN ? selectedValue : '',
      dob: formattedDate,
      id:
        selectedIdentifierType === IdentifierType.MEDICARE_ID ||
        selectedIdentifierType === IdentifierType.MEDICAID_ID
          ? selectedValue
          : '',
      planType: getPlanTypeByIdentifier(selectedIdentifierType),
      memberId: selectedIdentifierType === IdentifierType.MEMBER_ID ? selectedValue : '',
    };

    try {
      const response = await verifyProxy(
        getDrVerifyRequest(proxyAuthorizationLinkData, {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
        }, formattedDate)
      );

      // Check for 401 Unauthorized and immediately sign out
      if (logoutErrorHandler(response?.errorCode)) {
        return;
      }

      if (isNoContentResponse(response)) {
        openDrVerifyFailureModal();
        formSubmissionFailed({
          segment: `${domainForSegment}${pathname}`,
          form: {
            ...AdobeFormConfig['personalInfoError'],
            formFailReason: t('activation.formFailReason'),
          },
        });
        return;
      }

      if (!response || response.status < 200 || response.status >= 300 || response.error || response.errorCode) {
        setLoader(false);
        formSubmissionFailed({
          segment: `${domainForSegment}${pathname}`,
          form: {
            ...AdobeFormConfig['personalInfoError'],
            formFailReason: t('activation.formFailReason'),
          },
        });
        return;
      }

      let registrationVerifyResponse: any;

      try {
        registrationVerifyResponse = await PersonalInformation(
          registrationVerifyPayload
        );
      } catch (error) {
        if (
          shouldRedirectDrToGuestRegistration(
            error,
            proxyAuthorizationLinkData,
            true
          )
        ) {
          redirectToGuestRegistration(data);
          return;
        }

        setLoader(false);
        setIdentifierError(t('activation.formFailReason'));
        formSubmissionFailed({
          segment: `${domainForSegment}${pathname}`,
          form: {
            ...AdobeFormConfig['personalInfoError'],
            formFailReason: t('activation.formFailReason'),
          },
        });
        return;
      }

      if (registrationVerifyResponse?.error) {
        logPersonalInfoError(registrationVerifyResponse);
      }

      // Check for 401 Unauthorized and immediately sign out
      if (logoutErrorHandler(registrationVerifyResponse?.errorCode)) {
        return;
      }

      if (isNoContentResponse(registrationVerifyResponse)) {
        if (
          shouldRedirectDrToGuestRegistration(
            registrationVerifyResponse,
            proxyAuthorizationLinkData,
            true
          )
        ) {
          redirectToGuestRegistration(data);
          return;
        }

        setLoader(false);
        setIdentifierError(t('common.serviceErrorRequestId'));
        formSubmissionFailed({
          segment: `${domainForSegment}${pathname}`,
          form: {
            ...AdobeFormConfig['personalInfoError'],
            formFailReason: t('activation.formFailReason'),
          },
        });
        return;
      }

      if (
        registrationVerifyResponse?.data?.isActivated === true ||
        registrationVerifyResponse?.error ||
        !registrationVerifyResponse?.data
      ) {
        setLoader(false);
        setIdentifierError(
          registrationVerifyResponse?.data?.isActivated === true
            ? ''
            : t('activation.formFailReason')
        );
        setAlreadyRegistered(
          registrationVerifyResponse?.data?.isActivated === true
        );
        formSubmissionFailed({
          segment: `${domainForSegment}${pathname}`,
          form: {
            ...AdobeFormConfig['personalInfoError'],
            formFailReason:
              registrationVerifyResponse?.data?.isActivated === true
                ? 'It appears you already have an account with us'
                : t('activation.formFailReason'),
          },
        });
        return;
      }

      setFormData({
        ...formData,
        [page]: {
          ...data,
          ...registrationVerifyPayload,
          dob: convertStringToDate(data.dob),
        },
      });
      await setOpenReplayRegistrationTags({
        firstName: data.firstName,
        lastName: data.lastName,
        dob: data.dob,
      });
      formSubmissionSucceeded({
        segment: `${domainForSegment}${pathname}`,
        form: AdobeFormConfig['personalInfoSuccess'],
      });
      setPage((currPage: number) => currPage + 1);
      setLoader(false);
    } catch (error) {
      if (isNoContentResponse(error)) {
        openDrVerifyFailureModal();
        formSubmissionFailed({
          segment: `${domainForSegment}${pathname}`,
          form: {
            ...AdobeFormConfig['personalInfoError'],
            formFailReason: t('activation.formFailReason'),
          },
        });
        return;
      }

      setLoader(false);
      setIdentifierError(t('activation.formFailReason'));
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['personalInfoError'],
          formFailReason: t('activation.formFailReason'),
        },
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form data-testid="submit_form" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        {loader && <Loader />}
        <div className="row">
          <div className="col-lg-6 col-md-8 col-12 mt-n2">
            {FormTitles[page] !== '' && (
              <>
                <span className="visually-hidden me-1">
                  {t('common.registrationCurrentPage')} {[page + 1]}
                </span>
                <Headings
                  level={1}
                  text={t(FormTitleTranslationKeys[FormTitles[page]])}
                  variant="semiBold"
                  dataTestId="registrationHeading"
                  className="mb-4 pb-4"
                />
              </>
            )}

            {alreadyRegistered && (
              <div className="mb-1">
                <GlobalError data-testid="memberExistId" variant="mb-4">
                  <Paragraph
                    text={
                      <>
                        {t('activation.alreadyRegister')}
                        <Link to={NavigateConst.signIn} className="px-1 d-inline-block">
                          {t('activation.sign')}
                        </Link>
                        {t('activation.alreadyRegister2')}
                        <Link to={NavigateConst.forgotUsername} className="px-1 d-inline-block">
                          {t('common.userName')}
                        </Link>
                        {t('activation.alreadyRegister3')}
                        <Link to={NavigateConst.resetPassword} className="px-1 d-inline-block">
                          {t('common.password')}
                        </Link>
                        {t('activation.alreadyRegister4')}
                      </>
                    }
                    variant="regular"
                    className="mb-0"
                  />
                </GlobalError>
              </div>
            )}

            {identifierError && <ErrorMessage>{identifierError}</ErrorMessage>}

            <div className="mb-3">
              <Label className="pb-1" htmlFor="firstName">
                {t('activation.firstNameLabel')}
              </Label>
              <InputField
                id="firstName"
                data-testid="firstName"
                maxLength={getMaxLength.name}
                placeholder={t('activation.firstNamePlaceholder')}
                {...register('firstName', {
                  required: true,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue('firstName', e.target.value.replace(SpecialCharacter, ''));
                  },
                })}
              />
              {errors.firstName && <ErrorMessage>{t('activation.firstNameError')}</ErrorMessage>}
            </div>

            <div className="mb-3">
              <Label className="pb-1" htmlFor="lastName">
                {t('activation.lastNameLabel')}
              </Label>
              <InputField
                id="lastName"
                data-testid="lastName"
                maxLength={getMaxLength.name}
                placeholder={t('activation.lastNamePlaceholder')}
                {...register('lastName', {
                  required: true,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue('lastName', e.target.value.replace(SpecialCharacter, ''));
                  },
                })}
              />
              {errors.lastName && <ErrorMessage>{t('activation.lastNameError')}</ErrorMessage>}
            </div>

            <div className="mb-3">
              <Label className="pb-1" dataTestId="dateOfBirthInputLabel" htmlFor="dob">
                {t('common.dateOfBirth')}
              </Label>
              <DatePicker
                selectedValue={formData[page]?.dob ?? ''}
                maxLength={getMaxLength.dob}
                fieldName={'dob'}
                id="dob"
                data-testid="dob"
                placeholder={t('common.dateOfBirthPlaceholder')}
                maxDate={setMaxDate(minYears)}
                updateValue={(value: string) => {
                  setValue('dob', value, {
                    shouldValidate: true,
                  });
                  clearErrors('dob');
                }}
                errorClass={errors?.dob ? 'error-border' : ''}
                {...register('dob', {
                  required: true,
                  validate: {
                    dateFormat: (value: string) => {
                      if (DateFormatRegex.test(value) && value !== '') {
                        return true;
                      }
                      const dateFormatError = String(
                        t('forgotUsername.dateofbirthFormat') ?? ''
                      );
                      return dateFormatError;
                    },
                    minAge: (value: string) => {
                      const isMinAgeValid = validateDatePickerInput(value, minYears);
                      if (isMinAgeValid) {
                        return true;
                      }
                      return false;
                    },
                  },
                })}
              />
              {errors.dob && (
                <ErrorMessage>
                  {errors.dob.type === 'minAge' ? t('activation.lessAge') : errors.dob.message || t('common.dobRequired')}
                </ErrorMessage>
              )}
            </div>

            {isAvMedBrand ? (
              <div className="mb-3">
                <Label className="pb-1" htmlFor="ssn">
                  {t('activation.socialSecurityNumber')}
                </Label>
                <InputField
                  id="ssn"
                  data-testid="ssn"
                  maxLength={getMaxLength.ssn}
                  placeholder={t('common.ssnPlaceHolder')}
                  {...register('ssn', {
                    required: true,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      const sanitizedSsn = String(e?.target?.value ?? '').replaceAll(/\D/g, '');
                      setValue('ssn', sanitizedSsn);
                    },
                  })}
                />
                {errors.ssn && <ErrorMessage>{t('common.ssnRequired')}</ErrorMessage>}
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <Paragraph
                    text={t('activation.chooseIdentifier')}
                    variant="bold"
                    className="mb-2"
                  />
                  {identifierOptions.map((option, index) => (
                    <div
                      key={option.value}
                      className={index < identifierOptions.length - 1 ? 'mb-2' : ''}
                    >
                      <div className="d-flex align-items-center p-1">
                        <Checkbox
                          level={3}
                          id={`${option.value}Radio`}
                          type="radio"
                          data-testid={`${option.value}Radio`}
                          checked={identifierType === option.value}
                          onChange={() => handleIdentifierChange(option.value)}
                        >
                          {option.label}
                        </Checkbox>
                      </div>
                    </div>
                  ))}
                </div>

                {(() => {
                  const currentConfig = identifierFieldConfig[identifierType] as any;
                  if (!currentConfig?.id || !isIdentifierFieldKey(currentConfig.id)) {
                    return null;
                  }
                  const currentFieldId = currentConfig.id;
                  const fieldError = errors[currentFieldId as keyof typeof errors];

                  return (
                    <div className="mb-3">
                      <Label className="pb-1" htmlFor={currentFieldId}>
                        {currentConfig.label}
                      </Label>
                      <div className="d-flex align-items-center">
                        <div className="position-relative flex-1">
                          <InputField
                            id={currentFieldId}
                            data-testid={currentFieldId}
                            maxLength={currentConfig.maxLength}
                            placeholder={currentConfig.placeholder}
                            className={`${fieldError ? 'error-border' : ''}`}
                            {...register(currentFieldId, {
                              required: true,
                              minLength: currentConfig.minLength
                                ? {
                                    value: currentConfig.minLength,
                                    message: t('common.ssnRequired'),
                                  }
                                : undefined,
                              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                (setValue as any)(
                                  currentFieldId,
                                  e.target.value.replace(currentConfig.restrictInput, '')
                                );
                              },
                            })}
                          />
                        </div>
                        <div className={`${fieldError ? 'mt-n3' : ''} ms-2`}>
                          <Button
                            variant="icon_btn"
                            data-testid={`${currentFieldId}HelpIcon`}
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                              e.preventDefault();
                              setIdentifierTooltipModal(true);
                            }}
                          >
                            <HelpIcon />
                          </Button>
                        </div>
                      </div>
                      {fieldError && (
                        <ErrorMessage>
                          {(fieldError as any)?.message || currentConfig.errorMessage}
                        </ErrorMessage>
                      )}
                    </div>
                  );
                })()}
              </>
            )}

            <div className="mb-3">
              <Label className="pb-1" htmlFor="email">
                {t('common.email')}
              </Label>
              <InputField
                id="email"
                data-testid="email"
                type="email"
                maxLength={getMaxLength.email}
                placeholder={t('common.emaillblPlaceholder')}
                {...register('email', {
                  required: true,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('common.invalidEmail'),
                  },
                })}
              />
              {errors.email && <ErrorMessage>{errors.email.message || t('common.emailRequired')}</ErrorMessage>}
            </div>

            <div className="mb-3">
              <Label className="pb-1" htmlFor="confirmEmail">
                {t('common.confirmEmail')}
              </Label>
              <InputField
                id="confirmEmail"
                data-testid="confirmEmail"
                type="email"
                maxLength={getMaxLength.email}
                placeholder={t('activation.confirmEmailLabel')}
                {...register('confirmEmail', {
                  required: true,
                  validate: (value) => value === watch('email') || t('common.emailMismatch'),
                })}
              />
              {errors.confirmEmail && <ErrorMessage>{errors.confirmEmail.message || t('common.confirmEmailRequired')}</ErrorMessage>}
            </div>

            <div className="row pt-4 pb-2">
              <div className="col-md-auto col-12 d-grid gap-2 d-md-block mb-4">
                <Button dataTestId="continueButton" variant="primary_btn" className="btn-md-width" disabled={!isValid} type="submit">
                  {t('common.continue')}
                </Button>
              </div>
              <div className="col-md-auto col-12 d-grid gap-2 d-md-block mb-4">
                <Button
                  dataTestId="cancelButton"
                  variant="secondary_btn"
                  className="btn-md-width"
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    navigate(NavigateConst.signIn);
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {!isAvMedBrand && (
          <ModalMultiUse
            header={identifierFieldConfig[identifierType]?.label}
            btnClose={true}
            body={
              <Paragraph
                text={identifierFieldConfig[identifierType]?.tooltipText}
                variant="regular"
                className="mb-0"
              />
            }
            footer={
              <div className="col-12 px-0">
                <Button
                  dataTestId="identifierTooltipCloseButton"
                  variant="primary_btn"
                  className="btn-md-height w-100"
                  onClick={() => setIdentifierTooltipModal(false)}
                >
                  {t('common.buttonOk')}
                </Button>
              </div>
            }
            dataTestId="identifierTooltipModal"
            variant="defaultNoPyBody"
            isOpen={identifierTooltipModal}
            onClose={() => setIdentifierTooltipModal(false)}
          />
        )}

        <ModalMultiUse
          header={t('profile.designatedRepresentative.drVerifyFailureTitle')}
          btnClose={true}
          body={
            <Paragraph
              text={t('profile.designatedRepresentative.drVerifyFailureDescription')}
              variant="regular"
              className="mb-0"
            />
          }
          footer={
            <div className="col-12 px-0">
              <Button
                dataTestId="drVerifyFailureTryAgainButton"
                variant="primary_btn"
                className="btn-md-height w-100"
                onClick={closeDrVerifyFailureModal}
              >
                {t('common.tryAgain')}
              </Button>
            </div>
          }
          dataTestId="drVerifyFailureModal"
          data-testid="drVerifyFailureModal"
          variant="defaultNoPyBody"
          isOpen={drVerifyFailureModal}
          onClose={closeDrVerifyFailureModal}
        />
      </form>
    </FormProvider>
  );
};

export default DrPersonalInformation;
